import { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, FileText, ChevronDown, Loader2 } from 'lucide-react';
import clsx from 'clsx';

/**
 * ExportDropdown - Reusable export butonu ve dropdown menüsü
 * 
 * @example
 * <ExportDropdown
 *   onExportExcel={() => exportToExcel({ data, columns, fileName: 'rapor' })}
 *   onExportPDF={() => exportToPDF({ elementRef, fileName: 'rapor' })}
 *   isExporting={isExporting}
 *   disabled={data.length === 0}
 * />
 */
export default function ExportDropdown({
    onExportExcel,
    onExportPDF,
    isExporting = false,
    disabled = false,
    variant = 'default', // 'default' | 'compact' | 'icon-only'
    position = 'bottom-right', // 'bottom-right' | 'bottom-left'
    className = '',
}) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Dışarı tıklama ile kapat
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleExcel = async () => {
        setIsOpen(false);
        await onExportExcel?.();
    };

    const handlePDF = async () => {
        setIsOpen(false);
        await onExportPDF?.();
    };

    const positionClasses = {
        'bottom-right': 'right-0',
        'bottom-left': 'left-0',
    };

    // Sadece bir seçenek varsa direkt buton
    const hasOnlyExcel = onExportExcel && !onExportPDF;
    const hasOnlyPDF = onExportPDF && !onExportExcel;
    const hasBoth = onExportExcel && onExportPDF;

    // Tek seçenek varsa dropdown gösterme
    if (hasOnlyExcel || hasOnlyPDF) {
        return (
            <button
                onClick={hasOnlyExcel ? handleExcel : handlePDF}
                disabled={disabled || isExporting}
                className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors',
                    'bg-bg-secondary border border-border-light hover:bg-bg-hover',
                    'text-text-secondary hover:text-text-primary',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    className
                )}
            >
                {isExporting ? (
                    <Loader2 size={16} className="animate-spin" />
                ) : hasOnlyExcel ? (
                    <FileSpreadsheet size={16} />
                ) : (
                    <FileText size={16} />
                )}
                {variant !== 'icon-only' && (
                    <span>{hasOnlyExcel ? 'Excel' : 'PDF'}</span>
                )}
            </button>
        );
    }

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                disabled={disabled || isExporting}
                className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors',
                    'bg-bg-secondary border border-border-light hover:bg-bg-hover',
                    'text-text-secondary hover:text-text-primary',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    className
                )}
            >
                {isExporting ? (
                    <Loader2 size={16} className="animate-spin" />
                ) : (
                    <Download size={16} />
                )}
                {variant !== 'icon-only' && (
                    <>
                        <span>{variant === 'compact' ? '' : 'Dışa Aktar'}</span>
                        <ChevronDown size={14} className={clsx('transition-transform', isOpen && 'rotate-180')} />
                    </>
                )}
            </button>

            {/* Dropdown Menu */}
            {isOpen && hasBoth && (
                <div className={clsx(
                    'absolute top-full mt-1 z-50 min-w-[160px]',
                    'bg-bg-secondary border border-border-light rounded-lg shadow-lg',
                    'py-1 overflow-hidden',
                    positionClasses[position]
                )}>
                    {onExportExcel && (
                        <button
                            onClick={handleExcel}
                            className={clsx(
                                'w-full flex items-center gap-3 px-4 py-2.5 text-left',
                                'text-text-secondary hover:text-text-primary hover:bg-bg-hover',
                                'transition-colors text-sm'
                            )}
                        >
                            <FileSpreadsheet size={18} className="text-success" />
                            <div>
                                <p className="font-medium">Excel</p>
                                <p className="text-xs text-text-muted">.xlsx formatı</p>
                            </div>
                        </button>
                    )}
                    {onExportPDF && (
                        <button
                            onClick={handlePDF}
                            className={clsx(
                                'w-full flex items-center gap-3 px-4 py-2.5 text-left',
                                'text-text-secondary hover:text-text-primary hover:bg-bg-hover',
                                'transition-colors text-sm'
                            )}
                        >
                            <FileText size={18} className="text-error" />
                            <div>
                                <p className="font-medium">PDF</p>
                                <p className="text-xs text-text-muted">.pdf formatı</p>
                            </div>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
